import React, { useState, useEffect, useRef } from 'react'
import Editor from '@monaco-editor/react'
import { API_URL } from '../../modules/auth/core/_requests'

interface LiveTextProps {}

const LiveText: React.FC<LiveTextProps> = () => {
  const [code, setCode] = useState('# Welcome to Live Text Editor\n# Start typing your Python code here...\n\nprint("Hello, World!")\n\ndef greet(name):\n    return f"Hello, {name}!"\n\nif __name__ == "__main__":\n    message = greet("World")\n    print(message)')
  const debounceRef = useRef<NodeJS.Timeout>()
  const editorRef = useRef<any>(null)
  const [connectionStatus, setConnectionStatus] = useState<'testing' | 'connected' | 'disconnected'>('testing')
  const [authKey, setAuthKey] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authError, setAuthError] = useState(false)

  // Test backend connection
  const testConnection = async () => {
    try {
      console.log('🔍 Testing backend connection to:', `${API_URL}/live-text/status`)
      const response = await fetch(`${API_URL}/live-text/status`, {
        headers: {
          'Authorization': `Bearer ${authKey}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Backend connected:', data)
        setConnectionStatus('connected')
        setIsAuthenticated(true)
        return true
      } else if (response.status === 401) {
        console.error('❌ Authentication failed: Invalid key')
        setConnectionStatus('disconnected')
        setAuthError(true)
        setIsAuthenticated(false)
        return false
      } else {
        console.error('❌ Backend responded with error:', response.status, response.statusText)
        setConnectionStatus('disconnected')
        setIsAuthenticated(false)
        return false
      }
    } catch (error) {
      console.error('❌ Backend connection failed:', error)
      setConnectionStatus('disconnected')
      setIsAuthenticated(false)
      return false
    }
  }

  // Debounced POST request function
  const sendUpdate = async (text: string) => {
    if (!isAuthenticated) {
      console.log('⚠️ Not authenticated, skipping update')
      return
    }

    try {
      console.log('📤 Sending update to backend...')
      const response = await fetch(`${API_URL}/live-text/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          console.error('❌ Authentication failed during update')
          setIsAuthenticated(false)
        }
        console.error('Failed to send update:', response.statusText)
        setConnectionStatus('disconnected')
      } else {
        const result = await response.json()
        console.log('✅ Live text updated:', result)
        setConnectionStatus('connected')
      }
    } catch (error) {
      console.error('Error sending update:', error)
      setConnectionStatus('disconnected')
    }
  }

  // Handle editor change with debouncing
  const handleChange = (value: string | undefined) => {
    const newValue = value || ''
    setCode(newValue)

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Set new timeout for debounced API call
    debounceRef.current = setTimeout(() => {
      sendUpdate(newValue)
    }, 500) // 500ms debounce delay
  }

  // Handle editor mount
  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor
    
    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      lineHeight: 20,
      fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'off',
      lineNumbers: 'on',
      glyphMargin: false,
      folding: false,
      lineDecorationsWidth: 0,
      lineNumbersMinChars: 3,
    })
  }

  // Trigger authentication attempt
  const attemptAuth = () => {
    if (!authKey) return
    testConnection()
  }

  // Handle auth key change
  const handleAuthKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newKey = e.target.value.trim()
    setAuthKey(newKey)
    setIsAuthenticated(false)
    setAuthError(false)
    setConnectionStatus('disconnected')
  }

  // Handle Enter key to authenticate
  const handleAuthKeyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      attemptAuth()
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    // Don't auto-test connection - wait for auth key
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <div className="live-text-container">
      <style>{`
        .live-text-container {
          min-height: 100vh;
          background: #1e1e1e;
          color: #d4d4d4;
          font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
          display: flex;
          flex-direction: column;
        }

        .live-text-header {
          background: #2d2d30;
          padding: 1rem 2rem;
          border-bottom: 1px solid #3e3e42;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 2rem;
        }

        .header-left {
          flex: 1;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .auth-input {
          background: #1e1e1e;
          border: 1px solid #3e3e42;
          color: #d4d4d4;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          font-size: 0.9rem;
          min-width: 200px;
          font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
        }

        .auth-input:focus {
          outline: none;
          border-color: #007acc;
        }

        .auth-input.authenticated {
          border-color: #4ade80;
        }

        .auth-input.error {
          border-color: #ef4444;
        }

        .auth-status {
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .auth-status.authenticated {
          color: #4ade80;
        }

        .auth-status.error {
          color: #ef4444;
        }

        .auth-status.pending {
          color: #fbbf24;
        }

        .live-text-title {
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
        }

        .live-text-subtitle {
          color: #cccccc;
          font-size: 0.9rem;
          margin: 0.5rem 0 0 0;
        }

        .editor-container {
          flex: 1;
          background: #1e1e1e;
          overflow: hidden;
        }

        .monaco-editor-wrapper {
          height: 100%;
          position: relative;
        }

        .editor-disabled-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(30, 30, 30, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          backdrop-filter: blur(2px);
        }

        .editor-disabled-message {
          background: #2d2d30;
          color: #d4d4d4;
          padding: 2rem;
          border-radius: 8px;
          text-align: center;
          border: 1px solid #3e3e42;
        }

        .editor-disabled-message h3 {
          margin: 0 0 0.5rem 0;
          color: #fbbf24;
        }

        .editor-disabled-message p {
          margin: 0;
          color: #cccccc;
        }

        .status-bar {
          background: #007acc;
          color: white;
          padding: 0.5rem 2rem;
          font-size: 0.85rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .status-info {
          display: flex;
          gap: 1rem;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .connection-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }

        .connection-dot.connected {
          background-color: #4ade80;
        }

        .connection-dot.disconnected {
          background-color: #ef4444;
        }

        .connection-dot.testing {
          background-color: #fbbf24;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Monaco Editor custom styling */
        .monaco-editor .line-numbers {
          color: #858585 !important;
          background-color: #252526 !important;
        }

        .monaco-editor .monaco-editor-background {
          background-color: #1e1e1e !important;
        }

        .monaco-editor .current-line {
          background-color: rgba(255, 255, 255, 0.04) !important;
        }

        .monaco-editor .selected-text {
          background-color: rgba(173, 214, 255, 0.3) !important;
        }
      `}</style>

      {/* Header */}
      <div className="live-text-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="live-text-title">Live Text Editor</h1>
            <p className="live-text-subtitle">Python editor with real-time updates</p>
          </div>
          <div className="header-right">
            <div>
              <input
                type="password"
                placeholder="Enter auth key..."
                value={authKey}
                onChange={handleAuthKeyChange}
                onKeyDown={handleAuthKeyKeyDown}
                className={`auth-input ${isAuthenticated ? 'authenticated' : authError ? 'error' : ''}`}
              />
              <button
                className="btn btn-sm btn-primary"
                style={{height:'32px'}}
                onClick={attemptAuth}
                disabled={!authKey}
              >
                Auth
              </button>
              <div className={`auth-status ${isAuthenticated ? 'authenticated' : authError ? 'error' : 'pending'}`}>
                {isAuthenticated ? '✅ Authenticated' : authError ? '❌ Invalid Key' : '🔐 Enter Key'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="editor-container">
        <div className="monaco-editor-wrapper">
          <Editor
            height="100%"
            defaultLanguage="python"
            value={code}
            onChange={handleChange}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              fontSize: 14,
              lineHeight: 20,
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'off',
              lineNumbers: 'on',
              glyphMargin: false,
              folding: false,
              lineDecorationsWidth: 0,
              lineNumbersMinChars: 3,
              automaticLayout: true,
              tabSize: 4,
              insertSpaces: true,
              detectIndentation: false,
              readOnly: !isAuthenticated,
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                verticalScrollbarSize: 12,
                horizontalScrollbarSize: 12,
              },
            }}
          />
          {!isAuthenticated && (
            <div className="editor-disabled-overlay">
              <div className="editor-disabled-message">
                <h3>🔐 Authentication Required</h3>
                <p>Enter the authentication key above to start editing</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-info">
          <span>Python</span>
          <span>Lines: {code.split('\n').length}</span>
          <span>Characters: {code.length}</span>
        </div>
        <div className="connection-status">
          <span className={`connection-dot ${connectionStatus}`}></span>
          <span>
            {!isAuthenticated && 'Authentication Required'}
            {isAuthenticated && connectionStatus === 'connected' && 'Live Updates Active'}
            {isAuthenticated && connectionStatus === 'disconnected' && 'Backend Disconnected'}
            {isAuthenticated && connectionStatus === 'testing' && 'Testing Connection...'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default LiveText
